import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2, Edit, Mail, Download, MoreHorizontal, Trash2 } from "lucide-react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { updateUserRole, resendEmailVerification, downloadUserReport, deleteUser } from "@/data-access/users";

interface User {
  id: number;
  email: string;
  country: string | null;
  createdAt: string;
  mfaEnabled: boolean;
  emailVerified: boolean;
  ip: string | null;
  userAgent: string | null;
  roles: string[];
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface UsersQueryParams {
  page: number;
  limit: number;
  search?: string;
  sortBy: string;
  sortOrder: string;
}

interface StatsResponse {
  totalUsers: number;
  totalUsersRegisteredToday: number;
}

const fetchUserStats = async (): Promise<StatsResponse> => {
  const response = await fetch("/api/users/stats");
  return response.json();
};

const fetchUsers = async ({ page, limit, search, sortBy, sortOrder }: UsersQueryParams): Promise<UsersResponse> => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
    sortOrder,
    ...(search && { search }),
  });

  const response = await fetch(`/api/users?${queryParams}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return response.json();
};

export default function UsersTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const { user: currentUser } = useAuth();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, search, sortBy, sortOrder],
    queryFn: () => fetchUsers({ 
      page, 
      limit: 10, 
      search, 
      sortBy, 
      sortOrder 
    }),
    placeholderData: (previousData) => previousData,
  });

  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['userStats'],
    queryFn: fetchUserStats,
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const downloadReportMutation = useMutation({
    mutationFn: downloadUserReport,
    onSuccess: () => {
      toast({
        title: "Report downloaded",
        description: "User report has been downloaded successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isAdmin = currentUser?.roles?.includes('admin');

  return (
    <div className="space-y-6 container mx-auto max-w-screen-lg">
      {/* Stats Cards */}
      <div className="flex justify-center gap-4 w-full">
        <Card className="w-[300px]">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Registered Users</p>
              <p className="text-2xl font-bold">{statsData?.totalUsers ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="w-[300px]">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Registered Users Today</p>
              <p className="text-2xl font-bold">{statsData?.totalUsersRegisteredToday ?? 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between gap-4">
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-sm bg-background border border-input h-12 rounded-lg focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary text-foreground placeholder:text-muted-foreground transition-all duration-200"
        />
        {isAdmin && (
          <Button
            onClick={() => downloadReportMutation.mutate()}
            disabled={downloadReportMutation.isPending}
            variant="outline"
            className="h-12"
          >
            {downloadReportMutation.isPending ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Downloading...
              </span>
            ) : (
              <span className="flex items-center">
                <Download className="h-4 w-4 mr-2" />
                Download Report
              </span>
            )}
          </Button>
        )}
      </div>

      {/* Error Display */}
      {error instanceof Error && (
        <div className="text-red-500">
          {error.message}
        </div>
      )}

      {/* Users Table */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('email')}
              >
                Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead className="whitespace-nowrap">Roles</TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('country')}
              >
                Country {sortBy === 'country' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('mfaEnabled')}
              >
                MFA {sortBy === 'mfaEnabled' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('emailVerified')}
              >
                Email Verified {sortBy === 'emailVerified' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                Resend Email Verification
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('createdAt')}
              >
                Created At {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('ip')}
              >
                IP Address {sortBy === 'ip' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('userAgent')}
              >
                User Agent {sortBy === 'userAgent' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead className="whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="whitespace-nowrap">{user.email}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <RoleCell user={user} />
                </TableCell>
                <TableCell className="whitespace-nowrap">{user.country || '-'}</TableCell>
                <TableCell className="whitespace-nowrap">{user.mfaEnabled ? 'Enabled' : 'Disabled'}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Badge variant={user.emailVerified ? 'default' : 'destructive'}>
                    {user.emailVerified ? 'Verified' : 'Unverified'}
                  </Badge>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <ResendEmailButton user={user} />
                </TableCell>
                <TableCell className="whitespace-nowrap">{user.createdAt.split('T')[0]}</TableCell>
                <TableCell className="whitespace-nowrap">{user.ip || '-'}</TableCell>
                <TableCell className="max-w-xs truncate whitespace-nowrap" title={user.userAgent || '-'}>
                  {user.userAgent || '-'}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <UserActionsCell user={user} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2">
        <Button
          variant="outline"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
        >
          Previous
        </Button>
        <span className="py-2 px-4">
          Page {page} of {data?.pagination?.totalPages ?? 1}
        </span>
        <Button
          variant="outline"
          onClick={() => setPage(p => Math.min(data?.pagination?.totalPages ?? 1, p + 1))}
          disabled={page === (data?.pagination?.totalPages ?? 1) || isLoading}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// Role Cell Component for editing user roles
function RoleCell({ user }: { user: User }) {
  const [isEditing, setIsEditing] = useState(false);
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Check if current user is admin
  const isAdmin = currentUser?.roles?.includes('admin');
  
  const updateRoleMutation = useMutation({
    mutationFn: async (newRole: "basic" | "admin") => {
      return updateUserRole(user.id, { role: newRole });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Role updated",
        description: `User role has been updated successfully.`,
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  const handleRoleChange = (newRole: string) => {
    if (newRole === "basic" || newRole === "admin") {
      updateRoleMutation.mutate(newRole);
    }
  };
  
  const currentRole = user.roles && user.roles.length > 0 ? user.roles[0] : 'basic';
  
  if (!isAdmin) {
    // Non-admin users can only view roles
    return (
      <div className="flex flex-wrap gap-1">
        {user.roles && user.roles.length > 0 ? (
          user.roles.map(role => (
            <Badge key={role} variant={role === 'admin' ? 'destructive' : 'secondary'}>
              {role}
            </Badge>
          ))
        ) : (
          <Badge variant="secondary">basic</Badge>
        )}
      </div>
    );
  }
  
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Select
          value={currentRole}
          onValueChange={handleRoleChange}
          disabled={updateRoleMutation.isPending}
        >
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">basic</SelectItem>
            <SelectItem value="admin">admin</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsEditing(false)}
          disabled={updateRoleMutation.isPending}
        >
          Cancel
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-wrap gap-1">
        {user.roles && user.roles.length > 0 ? (
          user.roles.map(role => (
            <Badge key={role} variant={role === 'admin' ? 'destructive' : 'secondary'}>
              {role}
            </Badge>
          ))
        ) : (
          <Badge variant="secondary">basic</Badge>
        )}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsEditing(true)}
        className="h-6 w-6 p-0"
      >
        <Edit className="h-3 w-3" />
      </Button>
    </div>
  );
}

// Resend Email Button Component
function ResendEmailButton({ user }: { user: User }) {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Check if current user is admin
  const isAdmin = currentUser?.roles?.includes('admin');
  
  const resendEmailMutation = useMutation({
    mutationFn: async () => {
      return resendEmailVerification(user.id);
    },
    onSuccess: () => {
      toast({
        title: "Email sent",
        description: `Verification email has been sent to ${user.email}.`,
        className: "bg-white text-black border-zinc-200",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  if (!isAdmin) {
    return <span className="text-gray-400">-</span>;
  }
  
  // Don't show button if email is already verified
  if (user.emailVerified) {
    return <span className="text-gray-500 text-sm">Already verified</span>;
  }
  
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => resendEmailMutation.mutate()}
      disabled={resendEmailMutation.isPending}
      className="h-8"
    >
      {resendEmailMutation.isPending ? (
        <span className="flex items-center">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Sending...
        </span>
      ) : (
        <span className="flex items-center">
          <Mail className="h-3 w-3 mr-1" />
          Resend
        </span>
      )}
    </Button>
  );
}

// Actions Cell Component — dropdown menu with destructive user actions
function UserActionsCell({ user }: { user: User }) {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = currentUser?.roles?.includes('admin');
  // Reason: an admin cannot delete their own account (matches server guard).
  const isSelf = currentUser?.id === user.id;

  const deleteUserMutation = useMutation({
    mutationFn: async () => deleteUser(user.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userStats'] });
      toast({
        title: "User deleted",
        description: data.message,
      });
      setConfirmOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (!isAdmin) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <span className="sr-only">Open actions menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem
            disabled={isSelf}
            onSelect={(e) => {
              e.preventDefault();
              setConfirmOpen(true);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete user
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {user.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the user and all of their data — addresses,
              alerts, search/export history, and feedback. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteUserMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteUserMutation.mutate();
              }}
              disabled={deleteUserMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUserMutation.isPending ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
